import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  Switch,
  InputGroup,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  AccordionItem,
  Icon,
} from "@chakra-ui/react";
import {
  format as formatDate,
  parse as parseDate,
  parseISO as parseISODate,
} from "date-fns";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import { getCustomizedTimesByDelay } from "./helpers";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import SocialAccountPropType from "../../../_types/SocialAccount";
import PostEditorDateTimePicker from "./PostEditorDateTimePicker";
import { translate as trans } from "../Trans/Trans";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import { FaGlobe } from "react-icons/fa";

const delayUnits = ["minutes", "hours", "days"];
const defaultDelayUnit = delayUnits[0];

const PostEditorScheduleTimePicker = ({
  accounts,
  accountsIds,
  customizedTimes,
  delayTime,
  defaultTimezone,
  onCustomizedTimesChange,
  onDelayTimeChange,
  time,
  date,
  timezone,
  onTimeChange,
  allowEmptyTime,
}) => {
  const selectedAccounts = useMemo(
    () => accounts.filter((item) => accountsIds.includes(item.id)),
    [accounts, accountsIds]
  );

  const handleDelayTimeToggle = useCallback(() => {
    if (delayTime) {
      onDelayTimeChange?.(null);

      trackBasicEvent(
        "post-editor",
        "click",
        "schedule time picker - delay turn off"
      );
    } else {
      onDelayTimeChange?.({
        value: "5",
        unit: defaultDelayUnit,
      });

      trackBasicEvent(
        "post-editor",
        "click",
        "schedule time picker - delay turn on"
      );
    }
  }, [delayTime, onDelayTimeChange]);

  const handleDelayTimeValueChange = useCallback(
    (value) => {
      onDelayTimeChange({
        ...delayTime,
        value: String(value || 0),
      });

      trackBasicEvent(
        "post-editor",
        "click",
        "schedule time picker - delay value change"
      );
    },
    [delayTime, onDelayTimeChange]
  );

  const handleDelayTimeUnitChange = useCallback(
    (event) => {
      const unit = event.target.value || defaultDelayUnit;

      onDelayTimeChange({
        ...delayTime,
        unit,
      });

      trackBasicEvent(
        "post-editor",
        "click",
        "schedule time picker - delay unit change"
      );
    },
    [delayTime, onDelayTimeChange]
  );

  const handleCustomizedTimesChange = useCallback(
    (event) => {
      const value = parseInt(event.target.value);
      if (value) {
        const sortedAccountsIds = selectedAccounts.map((item) => item.id);

        onCustomizedTimesChange?.({
          ...getCustomizedTimesByDelay(
            sortedAccountsIds,
            date,
            time,
            timezone,
            delayTime?.unit,
            delayTime?.value
          ),
        });

        onDelayTimeChange?.(null);

        trackBasicEvent(
          "post-editor",
          "click",
          "schedule time picker - customized times turn on"
        );
      } else {
        onCustomizedTimesChange?.(null);

        trackBasicEvent(
          "post-editor",
          "click",
          "schedule time picker - customized times turn off"
        );
      }
    },
    [
      selectedAccounts,
      date,
      time,
      timezone,
      delayTime,
      onCustomizedTimesChange,
      onDelayTimeChange,
    ]
  );

  const handleCustomizeTimeChange = useCallback(
    (accountId) => (values) => {
      if (values) {
        onCustomizedTimesChange({
          ...customizedTimes,
          [accountId]: {
            date: values.date || "",
            time: values.time || "",
            timezone: values.timezone || "",
          },
        });

        trackBasicEvent(
          "post-editor",
          "click",
          "schedule time picker - customized time change"
        );
      }
    },
    [customizedTimes, onCustomizedTimesChange]
  );

  useEffect(() => {
    if (!(accountsIds?.length > 1)) {
      onDelayTimeChange?.(null);
      onCustomizedTimesChange(null);
    }
  }, []);

  return (
    <Box width="full">
      {accountsIds?.length > 1 ? (
        <Flex justifyContent="flex-end" marginBottom="2">
          <Select
            onChange={handleCustomizedTimesChange}
            value={customizedTimes ? 1 : 0}
            size="sm"
          >
            <option value={0}>
              {trans("post_editor.date_time.cancel_customized_times")}
            </option>
            <option value={1}>
              {trans("post_editor.date_time.use_customized_times")}
            </option>
          </Select>
        </Flex>
      ) : null}
      {customizedTimes ? (
        <Box paddingTop="2">
          <Accordion
            allowToggle
            borderBottom="1px solid"
            borderColor="whiteAlpha.400"
          >
            {selectedAccounts.map((selectedAccount) => (
              <AccordionItem key={selectedAccount.id}>
                <AccordionButton
                  background="none"
                  border="0"
                  borderTop="1px solid"
                  borderColor="whiteAlpha.400"
                  paddingX="0"
                  paddingY="2"
                >
                  <Flex
                    flex="1"
                    maxWidth="calc(100% - 1em - 0.5rem)"
                    textAlign="left"
                    alignItems="center"
                  >
                    <SocialAccountAvatar
                      src={selectedAccount.image}
                      name={selectedAccount.name}
                      socialType={selectedAccount.social_type}
                      socialTypeShown
                      size="sm"
                    />
                    <Flex
                      direction="column"
                      flex="1"
                      maxWidth="calc(100% - 32px)"
                      paddingLeft="3"
                      fontSize="xs"
                      lineHeight="1"
                      color="gray.100"
                    >
                      <Flex fontSize="sm" whiteSpace="nowrap" overflow="hidden">
                        <Box>{selectedAccount.name}</Box>
                        <Box marginLeft="2" color="gray.200">
                          {trans(
                            `social_types.${selectedAccount.social_type}.sub`
                          )}
                        </Box>
                      </Flex>
                      <Flex
                        alignItems="center"
                        marginTop="1"
                        whiteSpace="nowrap"
                        overflow="hidden"
                      >
                        <Box color="white" fontWeight="500">
                          {customizedTimes[selectedAccount.id]?.time
                            ? formatDate(
                                parseDate(
                                  customizedTimes[selectedAccount.id].time,
                                  "HH:mm",
                                  new Date()
                                ),
                                "p",
                                dateFnsOptions()
                              )
                            : trans("post_editor.date_time.not_set")}
                        </Box>
                        {customizedTimes[selectedAccount.id]?.date ? (
                          <Box
                            marginLeft="2"
                            color="gray.100"
                            textTransform="uppercase"
                          >
                            {formatDate(
                              parseISODate(
                                customizedTimes[selectedAccount.id].date
                              ),
                              "EEE, LLL do, yyyy",
                              dateFnsOptions()
                            )}
                          </Box>
                        ) : null}
                        {customizedTimes[selectedAccount.id]?.timezone || defaultTimezone ? (
                          <Flex
                            alignItems="center"
                            marginLeft="2"
                            color="gray.200"
                          >
                            <Icon as={FaGlobe} marginRight="1" />
                            <Box>
                              {(customizedTimes[selectedAccount.id]?.timezone || defaultTimezone).replaceAll("_", " ")}
                            </Box>
                          </Flex>
                        ) : null}
                      </Flex>
                    </Flex>
                  </Flex>
                  <AccordionIcon marginX="1" color="white" />
                </AccordionButton>
                <AccordionPanel padding="0">
                  <Flex paddingY="2" justifyContent="center">
                    <PostEditorDateTimePicker
                      date={customizedTimes[selectedAccount.id]?.date}
                      time={customizedTimes[selectedAccount.id]?.time}
                      timezone={customizedTimes[selectedAccount.id]?.timezone}
                      defaultTimezone={defaultTimezone}
                      required={!allowEmptyTime}
                      showTimeClearButton={allowEmptyTime}
                      onChange={handleCustomizeTimeChange(selectedAccount.id)}
                      marginLeft="-0.5em"
                    />
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      ) : (
        <Box>
          <Flex paddingY="2" justifyContent="center">
            <PostEditorDateTimePicker
              time={time}
              date={date}
              timezone={timezone}
              defaultTimezone={defaultTimezone}
              onChange={onTimeChange}
              required={!allowEmptyTime}
              showTimeClearButton={allowEmptyTime}
            />
          </Flex>
          {accountsIds?.length > 1 ? (
            <Box marginTop="2">
              <Flex alignItems="center" justifyContent="center">
                <Switch
                  id="delay-time"
                  size="sm"
                  isChecked={!!delayTime}
                  onChange={handleDelayTimeToggle}
                />
                <Box
                  as="label"
                  htmlFor="delay-time"
                  fontSize="sm"
                  margin="0"
                  marginLeft="2"
                  cursor="pointer"
                >
                  {trans("post_editor.date_time.add_delay_time")}
                </Box>
              </Flex>
              {delayTime ? (
                <Flex justifyContent="center" marginTop="2">
                  <InputGroup size="sm" width="6rem" marginRight="1">
                    <NumberInput
                      onChange={handleDelayTimeValueChange}
                      value={delayTime.value}
                      precision={0}
                      step={1}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </InputGroup>
                  <InputGroup size="sm" width="8rem">
                    <Select
                      onChange={handleDelayTimeUnitChange}
                      value={delayTime.unit}
                    >
                      {delayUnits.map((unit) => (
                        <option key={unit} value={unit}>
                          {trans(`post_editor.date_time.delay.${unit}`)}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                </Flex>
              ) : null}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

PostEditorScheduleTimePicker.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  accountsIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  customizedTimes: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      date: PropTypes.string,
      time: PropTypes.string,
      timezone: PropTypes.string,
    }),
  }),
  delayTime: PropTypes.shape({
    value: PropTypes.string,
    unit: PropTypes.oneOf(delayUnits),
  }),
  defaultTimezone: PropTypes.string,
  date: PropTypes.string,
  time: PropTypes.string,
  timezone: PropTypes.string,
  onTimeChange: PropTypes.func,
  onCustomizedTimesChange: PropTypes.func,
  onDelayTimeChange: PropTypes.func,
  allowEmptyTime: PropTypes.bool,
};

PostEditorScheduleTimePicker.defaultProps = {
  accountsIds: undefined,
  customizedTimes: undefined,
  delayTime: undefined,
  defaultTimezone: undefined,
  date: undefined,
  time: undefined,
  timezone: undefined,
  onTimeChange: undefined,
  onCustomizedTimesChange: undefined,
  onDelayTimeChange: undefined,
  allowEmptyTime: false,
};

export default PostEditorScheduleTimePicker;
